import React, { useEffect, useRef,useState } from "react";

export interface UserProfile {
  token: string;
  email: string;
  givenName: string;
  familyName: string;
  name: string;
}

export interface LohonoAuthProps {
  children: React.ReactNode;
  authUrl?: string;
  apiUrl?: string;
}

const LohonoAuth = ({
  children,
  authUrl = `https://auth.lohono.com?redirect_url=${window.location.href}`,
  apiUrl = "https://oi.lohono.com/api/v2/login",
}: LohonoAuthProps): JSX.Element | null => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const authAttempted = useRef(false);

  const saveToLocalStorage = (key: string, value: any) => {
    const stringifiedValue =
      typeof value === "string" ? value : JSON.stringify(value);
    localStorage.setItem(key, stringifiedValue);
  };

  const validateSession = async (userProfile: UserProfile) => {
    if (authAttempted.current) return;
    authAttempted.current = true;

    try {
      const response = await fetch(
        apiUrl || "https://oi-staging.lohono.com/api/v2/login",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            tokenId: userProfile.token,
            email: userProfile.email,
            givenName: userProfile.name,
            familyName: userProfile.name,
          }),
        },
      );

      if (!response.ok) {
        // Try to parse error response from API
        let errorMessage = "Session validation failed";
        try {
          const errorData = await response.json();
          errorMessage = errorData.meta.message || errorData.message || errorData.error || errorMessage;
        } catch {
          // If response is not JSON, use status text 
          errorMessage = response.statusText || errorMessage;
        }

        console.log("Final Error message:", errorMessage);
        setError(errorMessage);
        setIsLoading(false);
        // Show error for 3 seconds then redirect
        setTimeout(() => {
          authAttempted.current = false;
          window.location.href = authUrl || "https://auth.lohono.com";
        }, 3000);
        return;
      }

      const data = await response.json();
      saveToLocalStorage("session", data.token);
      saveToLocalStorage("userInfo", { ...userProfile, ...data });
      setIsAuthenticated(true);
    } catch (error) {
      console.error("Authentication failed:", error);
      const errorMessage = error instanceof Error ? error.message : "Network error occurred";
      setError(errorMessage);
      setIsLoading(false); 
      localStorage.removeItem("session");
      localStorage.removeItem("userInfo");
      // Show error for 3 seconds then redirect
      setTimeout(() => {
        authAttempted.current = false;
        window.location.href = authUrl || "https://auth.lohono.com";
      }, 3000);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    let mounted = true;

    const authenticate = async () => {
      if (authAttempted.current) return;

      const searchParams = new URLSearchParams(window.location.search);
      const userProfileParam = searchParams.get("userProfile");

      if (userProfileParam) {
        try {
          const decodedProfile: UserProfile = JSON.parse(
            atob(userProfileParam),
          );
          await validateSession(decodedProfile);
        } catch (error) {
          console.error("Failed to decode user profile:", error);
          window.location.href = authUrl || "https://auth.lohono.com";
        }
        return;
      }

      const session = localStorage.getItem("session");
      const userInfo = localStorage.getItem("userInfo");

      if (!session || !userInfo) {
        window.location.href = authUrl || "https://auth.lohono.com";
        return;
      }

      if (mounted) {
        setIsAuthenticated(true);
        setIsLoading(false);
      }
    };

    authenticate();

    return () => {
      mounted = false;
    };
  }, []);

  console.log("Render - isLoading:", isLoading, "error:", error); // ← ADD THIS

  if (isLoading) {
    return (
      <div className="auth-overlay">
        <div className="auth-message">Authenticating, please wait...</div>
      </div>
    );
  }

  if (error && !isLoading) {
    return (
      <div className="auth-overlay">
        <div className="auth-message">{error}</div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : null;
};

export default LohonoAuth;